import React, { useEffect, useMemo, useState } from "react";
import YouTube from "react-youtube";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { BsPlayCircle } from "react-icons/bs";
import "./Videos.css";

const videos = [
  {
    title: "Trattoria di Brooklyn",
    videoId: "IenPyKYUnjs",
    eyebrow: "End-to-end restaurant ordering app",
    description:
      "Walkthrough of a full restaurant ordering system with customer and admin sides. The menu is loaded from MySQL, owners can add or remove items, customers can build a cart, and orders move through a Stripe purchase flow.",
    stack: ["React", "JavaScript", "Node.js", "MySQL", "Stripe"],
  },
  {
    title: "Stohos Angular",
    videoId: "YrigaGXr--E",
    eyebrow: "Angular business website",
    description:
      "Tour of an Angular and TypeScript information site for a shooting range, including gallery, contact-oriented pages, and server-side work that was disabled for the public demo environment.",
    stack: ["Angular 13", "TypeScript", "Node.js", "HTML/CSS"],
  },
  {
    title: "Task Manager",
    videoId: "kT4uMlV05Cw",
    eyebrow: "Task tracking workflow",
    description:
      "Demo of a task management app for storing, deleting, and tracking weekly work items, with a simple completion workflow and persistent backend data.",
    stack: ["Angular 13", "Node.js", "MongoDB", "HTML/CSS"],
  },
  {
    title: "Yfansis Garment LTD",
    videoId: "smRr8co_qEQ",
    eyebrow: "Legacy business website demo",
    description:
      "Archived walkthrough of a previous business website project, kept as a video reference for layout, presentation, and earlier client-facing work.",
    stack: ["Website", "Gallery", "Contact"],
  },
];

function normalizeTitle(title = "") {
  return title.trim().toLowerCase();
}

function Videos() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 800px)");
  const requestedVideoTitle = new URLSearchParams(location.search).get("video") || location.state?.videoTitle;

  const initialVideo = useMemo(
    () => videos.find((video) => normalizeTitle(video.title) === normalizeTitle(requestedVideoTitle)) || videos[0],
    [requestedVideoTitle]
  );
  const [currentVideo, setCurrentVideo] = useState(initialVideo);

  useEffect(() => {
    setCurrentVideo(initialVideo);
  }, [initialVideo]);

  const selectVideo = (video) => {
    setCurrentVideo(video);
    navigate(`/videos?video=${encodeURIComponent(video.title)}`, { replace: true });
  };

  const opts = {
    height: isMobile ? "250" : "500",
    width: "100%",
    playerVars: {
      autoplay: requestedVideoTitle ? 1 : 0,
    },
  };

  return (
    <Box id="videoContainer" className="fade-in">
      <Box className="video-shell">
        <Box className="video-main">
          <Box className="video-heading">
            <Typography component="span" className="video-kicker">
              {currentVideo.eyebrow}
            </Typography>
            <Typography component="h1">{currentVideo.title}</Typography>
          </Box>

          <Box id="actualPlayer" className="video-player-frame">
            <YouTube videoId={currentVideo.videoId} opts={opts} />
          </Box>

          <Box className="video-description-panel">
            <Typography component="h2">Description</Typography>
            <Typography component="p">{currentVideo.description}</Typography>
            <Box className="video-stack-list">
              {currentVideo.stack.map((item) => (
                <Typography key={item} component="span">
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>

        <aside className="video-list-panel">
          <Box className="video-list-header">
            <Typography component="span">Videos</Typography>
          </Box>
          <List disablePadding>
            {videos.map((video) => (
              <ListItem key={video.videoId} disablePadding>
                <ListItemButton
                  onClick={() => selectVideo(video)}
                  selected={video.videoId === currentVideo.videoId}
                  className="video-list-button"
                >
                  <Box className="video-list-icon">
                    <BsPlayCircle />
                  </Box>
                  <ListItemText
                    primary={video.title}
                    secondary={video.eyebrow}
                    primaryTypographyProps={{ fontWeight: 800 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </aside>
      </Box>
    </Box>
  );
}

export default Videos;
